import { debounce } from 'lodash';
import { some, chain } from 'obliterator';
import 'symbol-observable';
import type { ObservableLike, Observer, Unsubscribable } from 'type-fest';

import {
  callCompleteObservers,
  callErrorObservers,
  callNextAndCompleteObservers,
  callNextObservers,
  isIterable,
  subscribeObserver,
  noopUnsubscribe,
} from '@frontend/utils';

const millisecondsUnit = 'milliseconds' as const;

//ProgressSignal allows observing the progress being tracked by a ProgressController
//* Implements Observable-Like (See https://github.com/sindresorhus/type-fest/blob/main/source/observable-like.d.ts)
//  * One or more subscriptions can be created to listen to progress of a what-wg fetch `response`
//  * It is not necessary to create subscriptions.  Observing with subscriptions means
//    updates are pushed.  The observer's `next` handler could be debounced if pushing causes
//    performance issue.  But alternatively, it maybe better to poll/pull updates at the desired
//    cadence.
//* Not exported because it should always be constructed by ProgressController
//* Instance of ProgressSignal is on ProgressController
class ProgressSignal<T extends BaseProgressController>
  implements ObservableLike<ProgressSignal<T>>
{
  #usedBy = new Set<CompositeProgressController>();
  #progressController: T | undefined;

  constructor(progressController: T) {
    this.#progressController = progressController;
  }

  //Warning: It can be dangerous to dispose of signal. It should only be called by parent CompositeProgress Controller IFF
  //the parent is finished and has cached its finishedAt, todoCount, doneCount
  public disposeIfUnused(): boolean {
    if (this.#usedBy.size === 0) {
      this.#progressController = undefined;
      return true;
    }
    return false;
  }

  //dispose() must be done carefully! (If used by another controller, then the other controller no longer has visibility once disposed)
  //Best used when all visible items are visible.
  //Otherwise use disposeIfUnused()
  public dispose() {
    //Remove this signal from composite controller that uses it
    for (const c of this.#usedBy) {
      c.remove({ signal: this });
    }
    this.#usedBy.clear();
    this.#progressController = undefined;
  }

  public addUsedBy(c: CompositeProgressController) {
    this.#usedBy.add(c);
  }

  public removeUsedBy(c: CompositeProgressController) {
    return this.#usedBy.delete(c);
  }

  public removeFromUsedBy() {
    for (const c of this.#usedBy) {
      c.remove({ signal: this });
    }
  }

  public get id(): string | undefined {
    return this.#progressController?.id;
  }

  public get isActive(): boolean {
    return this.#progressController?.isActive ?? false;
  }

  public get stoppedBeforeFinished(): boolean {
    return this.#progressController?.stoppedBeforeFinished ?? false;
  }

  public get unit(): string {
    return this.#progressController?.unit ?? '';
  }

  public get expectedMs(): number {
    return this.#progressController?.expectedMs ?? 0;
  }

  public get isComposite(): boolean {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController;
  }

  public descendents() {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.descendents()
      : [];
  }

  public clear(clearDescendants = true) {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (this.#progressController instanceof CompositeProgressController) {
      this.#progressController.clear(clearDescendants);
    }
  }

  public clearIfFinished(clearDescendants = true) {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (this.#progressController instanceof CompositeProgressController) {
      this.#progressController.clearIfFinished(clearDescendants);
    }
  }

  public get isClosed(): boolean {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.isClosed
      : true;
  }

  public get hasItems(): boolean {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.hasItems
      : false;
  }

  public get items(): Iterable<AnyProgressSignal> {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.items
      : [];
  }

  public get itemWeightPairs(): Iterable<ItemAndWeight> {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.itemWeightPairs
      : [];
  }

  public get visibleItems(): Iterable<AnyProgressSignal> {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.visibleItems
      : [];
  }

  public get visibleItemWeightPairs(): Iterable<ItemAndWeight> {
    //eslint-disable-next-line @typescript-eslint/no-use-before-define
    return this.#progressController instanceof CompositeProgressController
      ? this.#progressController.visibleItemWeightPairs
      : [];
  }

  public get description(): string {
    return this.#progressController?.description ?? '';
  }

  public get hasStarted(): boolean {
    return this.#progressController?.hasStarted ?? false;
  }

  // startedAt: ms since unix epoch
  public get startedAt(): number | undefined {
    return this.#progressController?.startedAt;
  }

  // finishedAt: ms since unix epoch
  public get finishedAt(): number | undefined {
    return this.#progressController?.finishedAt;
  }

  // elapsedMs: elapsed ms since progressController was started OR time to finish in milliseconds
  public get elapsedMs(): number {
    return this.#progressController?.elapsedMs ?? 0;
  }

  public get elapsedSec(): number {
    return this.#progressController?.elapsedSec ?? 0;
  }

  // todoCount: Expected number of todo items (unit is not specified in progressController)
  // * In specific implementations of progressController, there may be an alias such as `todoCount`
  //   if we are tracking progress of `bytes` we expect to receive
  public get todoCount(): number | undefined {
    return this.#progressController?.todoCount;
  }

  // doneCount: Number of items done (unit is not specified in progressController)
  // * In specific implementations of progressController, there may be an alias such as `receivedBytes`
  //   if we are tracking progress of `bytes` we have received
  public get doneCount(): number {
    return this.#progressController?.doneCount ?? 0;
  }

  // remainder: Remainder of todo items (unit is not specified in progressController)
  // * In specific implementations of progressController, there may be an alias such as `remainderBytes`
  //   if we are tracking progress of `bytes` we expect to receive
  public get remainder(): number {
    return this.#progressController?.remainder ?? 0;
  }

  // rate: items per ms
  // * this rate is the average rate since this.startAt
  public get rate(): number {
    return this.#progressController?.rate ?? 0;
  }

  public get rateInSeconds(): number {
    return this.#progressController?.rateInSeconds ?? 0;
  }

  // progressRatio: a ratio between 0 and 1
  public get progressRatio(): number {
    return this.#progressController?.progressRatio ?? 0;
  }

  public get progressPercentage(): number {
    return this.#progressController?.progressPercentage ?? 0;
  }

  public get isLoadingVisible(): boolean {
    return this.#progressController?.isLoadingVisible ?? false;
  }

  // estimatedTimeRemainderMs: time in ms
  public get estimatedTimeRemainderMs(): number {
    return this.#progressController?.estimatedTimeRemainderMs ?? 0;
  }

  public get estimatedTimeRemainderSec(): number {
    return (this.#progressController?.estimatedTimeRemainderMs ?? 0) / 1000;
  }

  public get isStalled(): boolean | undefined {
    return (this.#progressController as { isStalled?: boolean })?.isStalled;
  }

  // estimatedFinishAt: ms since unix epoch
  public get estimatedFinishAt() {
    return this.#progressController?.estimatedFinishAt;
  }

  public subscribeIsLoadingVisible(
    observer?: Partial<Observer<VisibleProgressData>>,
  ): Unsubscribable {
    return this.#progressController
      ? this.#progressController.subscribeIsLoadingVisible(observer)
      : noopUnsubscribe;
  }

  public unsubscribeIsLoadingVisible(
    observer?: Partial<Observer<VisibleProgressData>>,
  ): boolean {
    return (
      this.#progressController?.unsubscribeIsLoadingVisible(observer) ?? false
    );
  }

  public subscribe(observer: Partial<Observer<T['signal']>>): Unsubscribable {
    return this.#progressController
      ? this.#progressController.subscribe(observer)
      : noopUnsubscribe;
  }

  public unsubscribe(observer: Partial<Observer<T['signal']>>): boolean {
    return this.#progressController?.unsubscribe(observer) ?? false;
  }

  // [Symbol.observable]() required for Observable-Like interface
  public [Symbol.observable](): this {
    return this;
  }

  public toJSON() {
    return {
      isStalled: this.isStalled,
      estimatedTimeRemainderSec: this.estimatedTimeRemainderSec,
      progressPercentage: this.progressPercentage,
      description: this.description,
    };
  }
}

export { type ProgressSignal }; //only export type. ProgressSignal should be constructed by a ProgressController only

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyProgressSignal = ProgressSignal<any>;

type ProgressControllerDescription =
  | string
  | (<C extends BaseProgressController>(context?: C) => string);

type BaseProgressControllerOptions = {
  id?: string;
  observers?: Iterable<Observer<AnyProgressSignal>>;
  unit?: string;
  description?: ProgressControllerDescription;
};

export type VisibleProgressData = {
  description: string;
  percentage: number;
} | null;

//BaseProgressController is an abstract class (Don't instantiate it. It's intended to be extended)
abstract class BaseProgressController
  implements ObservableLike<AnyProgressSignal>
{
  protected _subscriptions: Set<Partial<Observer<AnyProgressSignal>>> =
    new Set();
  protected isLoadingVisibleSubscriptions: Set<
    Partial<Observer<VisibleProgressData>>
  > = new Set();

  protected _startedAt?: number; //ms since unix epoch
  protected _finishedAt?: number; //ms since unix epoch
  protected _todoCount?: number;
  protected _doneCount = 0;
  protected _isActive = true;
  protected _description?: ProgressControllerDescription;
  protected _unit?: string; //unit for todoCount, doneCount and remainder (ie bytes, time)

  #signal: ProgressSignal<this> | undefined = new ProgressSignal(this); //Progress Signal allows observing the progress
  #id: string | undefined;
  _lastIsLoadingVisible = false;
  _lastVisibleDescription = '';
  _lastVisibleProgressPercentage = 0;
  _isLoadingObserver: Partial<Observer<AnyProgressSignal>> | undefined;

  constructor(options?: BaseProgressControllerOptions) {
    if (options) {
      if (typeof options.id === 'string') {
        this.#id = options.id;
      }
      if (isIterable(options.observers)) {
        for (const observer of options.observers) {
          this._subscriptions.add(observer);
        }
      }
      if (typeof options.unit === 'string') {
        this._unit = options.unit;
      }
      const typeDescription = typeof options.description;
      if (typeDescription === 'string' || typeDescription === 'function') {
        this._description = options.description;
      }
    }
  }

  //Warning: Don't dispose until parent has captured the todoCount/doneCount
  public disposeIfUnused(): boolean {
    if (!this.#signal || this.#signal.disposeIfUnused()) {
      this.#signal = undefined;
      if (this._isLoadingObserver) {
        this.unsubscribe(this._isLoadingObserver);
        this.isLoadingVisibleSubscriptions.clear();
        this._isLoadingObserver = undefined;
      }
      return true;
    }
    return false;
  }

  //dispose() must be done carefully! (If used by another controller, then the other controller no longer has visibility once disposed)
  //Best used when all visible items are visible.
  //Otherwise use disposeIfUnused()
  public dispose() {
    if (this.#signal) {
      this.#signal.dispose();
      this.#signal = undefined;
    }
    if (this._isLoadingObserver) {
      this.unsubscribe(this._isLoadingObserver);
      this.isLoadingVisibleSubscriptions.clear();
      this._isLoadingObserver = undefined;
    }
  }

  public get id(): string | undefined {
    return this.#id;
  }

  //signal: An Observable-Like of the progress being tracked by this ProgressController
  public get signal() {
    return this.#signal as ProgressSignal<this>;
  }

  //unit: The unit for todoCount, doneCount and remainder (ie bytes, time)
  public get unit(): string | undefined {
    return this._unit;
  }

  //description: Description of what we're tracking progress for
  public get description(): string {
    return typeof this._description === 'function'
      ? this._description(this)
      : typeof this._description === 'string'
      ? this._description
      : '';
  }

  // hasStarted: boolean. Progress has started
  public get hasStarted(): boolean {
    return Number.isSafeInteger(this.startedAt);
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  //stoppedBeforeFinished: boolean
  //progress will stop before finishing if there is an error
  public get stoppedBeforeFinished() {
    return !this.isActive && !this.finishedAt;
  }

  // startedAt: ms since unix epoch
  public get startedAt(): number | undefined {
    return this._startedAt;
  }

  // finishedAt: ms since unix epoch
  public get finishedAt(): number | undefined {
    return this._finishedAt;
  }

  // elapsedMs: elapsed ms since BaseProgressController was started OR time to finish in milliseconds
  public get elapsedMs(): number {
    const startedAt = this.startedAt;
    if (startedAt) {
      const finishedAt = this.finishedAt;
      if (finishedAt) {
        return finishedAt - startedAt;
      } else if (this.isActive) {
        const elapsedMs = Date.now() - startedAt;
        if (elapsedMs >= 1) {
          return elapsedMs;
        } else {
          //Sometimes elapsedMs is now === startedAt and doneCount > 0
          //* Sometimes happens with FetchResponseProgressController
          //* In order to calculate this.rate correctly, round elapsedMs up to 1ms
          //* Don't use this.doneCount because it can lead to infinite recurrsion
          if (this._doneCount > 0) {
            return 1;
          } else {
            return elapsedMs;
          }
        }
      } else {
        return NaN; //otherwise there was an error and it is not tracking
      }
    }
    return 0; //hasn't started
  }

  public get elapsedSec() {
    return this.elapsedMs / 1000;
  }

  // todoCount: Expected number of todo items (unit is not specified in BaseProgressController)
  // * In specific implementations of BaseProgressController, there may be an alias such as `todoCount`
  //   if we are tracking progress of `bytes` we expect to receive
  public get todoCount(): number | undefined {
    return this._todoCount;
  }

  // doneCount: Number of items done (unit is not specified in BaseProgressController)
  // * In specific implementations of BaseProgressController, there may be an alias such as `receivedBytes`
  //   if we are tracking progress of `bytes` we have received
  public get doneCount(): number {
    return this._doneCount;
  }

  // remainder: Remainder of todo items (unit is not specified in BaseProgressController)
  // * In specific implementations of BaseProgressController, there may be an alias such as `remainderBytes`
  //   if we are tracking progress of `bytes` we expect to receive
  public get remainder(): number {
    const todoCount = this.todoCount;
    const doneCount = this.doneCount;
    const remainder =
      typeof todoCount === 'number' &&
      typeof doneCount === 'number' &&
      todoCount > 0
        ? todoCount - doneCount
        : 0;
    return remainder > 0 ? remainder : 0;
  }

  // rate: items per ms
  // * this rate is the average rate since this.startAt
  public get rate(): number {
    //If the unit is milliseconds, then the weighted todoCount and doneCount are aligned and rate is 1 second per second
    if (this.unit === millisecondsUnit) {
      return 1;
    }
    const elapsedMs = this.elapsedMs;
    const doneCount = this.doneCount;
    return elapsedMs > 0 && typeof doneCount === 'number'
      ? doneCount / elapsedMs
      : 0;
  }

  public get rateInSeconds(): number {
    return this.rate * 1000; // (units/ms) * (1000 ms/sec) = units / sec
  }

  // progressRatio: a ratio between 0 and 1
  public get progressRatio(): number {
    if (this._finishedAt) {
      return 1;
    }
    const todoCount = this.todoCount;
    const doneCount = this.doneCount;
    return typeof todoCount === 'number' &&
      typeof doneCount === 'number' &&
      todoCount > 0
      ? Math.min(doneCount / todoCount, 1.0)
      : todoCount === 0
      ? 0
      : 0;
  }

  public get progressPercentage(): number {
    return Math.floor(this.progressRatio * 100);
  }

  protected get _isLoadingVisible(): boolean {
    return this.progressRatio < 1;
  }

  public get isLoadingVisible(): boolean {
    // If there are subscriptions, then #lastIsLoadingVisible is memoized and up to date
    // otherwise, fallback to the slower this._isLoadingVisible;
    return this.isLoadingVisibleSubscriptions.size
      ? this._lastIsLoadingVisible
      : this.isLoadingVisible;
  }

  public subscribeIsLoadingVisible(
    observer?: Partial<Observer<VisibleProgressData>>,
  ): Unsubscribable {
    if (!observer) {
      return noopUnsubscribe;
    }
    if (!this._isLoadingObserver) {
      this._isLoadingObserver = {
        next: debounce(this.loadingVisibleNextObserver.bind(this), 100, {
          maxWait: 100,
        }),
        error: debounce(this.loadingVisibleErrorObserver.bind(this), 100, {
          maxWait: 100,
        }),
        //not debounced for debugging
        // next: this.loadingVisibleNextObserver.bind(this),
        // error: this.loadingVisibleErrorObserver.bind(this),
      };
    }
    const { unsubscribe } = subscribeObserver(
      observer,
      this.isLoadingVisibleSubscriptions,
    );
    //subscription to parent is created/removed on demand so there are no unnecessary subscriptions
    if (this.isLoadingVisibleSubscriptions.size > 0) {
      this.subscribe(this._isLoadingObserver); //Will only be subscribed once because this._subscriptions is a Set
    }
    return {
      unsubscribe: () => {
        unsubscribe();
        if (
          this.isLoadingVisibleSubscriptions.size === 0 &&
          this._isLoadingObserver
        ) {
          this.unsubscribe(this._isLoadingObserver);
        }
      },
    };
  }

  public unsubscribeIsLoadingVisible(
    observer?: Partial<Observer<VisibleProgressData>>,
  ): boolean {
    if (!observer) {
      return false;
    }
    const result = this.isLoadingVisibleSubscriptions.delete(observer);
    if (
      this.isLoadingVisibleSubscriptions.size === 0 &&
      this._isLoadingObserver
    ) {
      this.unsubscribe(this._isLoadingObserver);
    }
    return result;
  }

  // estimatedTimeRemainderMs: time in ms
  public abstract estimatedTimeRemainderMs: number;

  // expectedMs: time in ms
  public abstract expectedMs: number;

  public abstract isStalled: boolean;

  // estimatedFinishAt: ms since unix epoch
  public get estimatedFinishAt() {
    if (this.finishedAt) {
      return this.finishedAt;
    } else {
      const estimatedTimeRemainderMs = this.estimatedTimeRemainderMs;
      return estimatedTimeRemainderMs >= 0
        ? Date.now() + estimatedTimeRemainderMs
        : NaN;
    }
  }

  public subscribe(
    observer: Partial<Observer<ProgressSignal<this>>>,
  ): Unsubscribable {
    if (!this.#signal) {
      return noopUnsubscribe;
    }
    if (!this._subscriptions.has(observer)) {
      callNextObservers([observer], this.signal); //call observer immediately with initial state
    }
    return subscribeObserver(observer, this._subscriptions, this.isActive);
  }

  public unsubscribe(
    observer: Partial<Observer<ProgressSignal<this>>>,
  ): boolean {
    return this._subscriptions.delete(observer);
  }

  // [Symbol.observable]() required for Observable-Like interface
  public [Symbol.observable]() {
    return this.#signal as ProgressSignal<this>;
  }

  protected _error(err: unknown, makeInactive = true) {
    if (this.isActive && !this.finishedAt) {
      const message =
        typeof err === 'string'
          ? err
          : err instanceof Error
          ? err.message
          : undefined;
      if (makeInactive) {
        if (message) {
          this._description = message;
        }
        this._isActive = false;
      }
      callErrorObservers(this._subscriptions, err);
      if (makeInactive) {
        this._subscriptions.clear(); //done with subscriptions
      }
    }
  }

  //To minimize load on observers (react useState setters):
  //* Only call observers IF there is a change to visibility
  //* And remember to debounce updates
  private loadingVisibleNextObserver() {
    const isLoadingVisible: boolean = this._isLoadingVisible;
    if (this._lastIsLoadingVisible !== isLoadingVisible) {
      callNextObservers(
        this.isLoadingVisibleSubscriptions,
        isLoadingVisible
          ? {
              description: (this._lastVisibleDescription = this.description),
              percentage: (this._lastVisibleProgressPercentage =
                this.progressPercentage),
            }
          : null,
      );
      this._lastIsLoadingVisible = isLoadingVisible;
    } else if (isLoadingVisible) {
      const description = this.description;
      if (this._lastVisibleDescription !== description) {
        callNextObservers(this.isLoadingVisibleSubscriptions, {
          description: (this._lastVisibleDescription = description),
          percentage: (this._lastVisibleProgressPercentage =
            this.progressPercentage),
        });
      } else {
        const percentage = this.progressPercentage;
        if (this._lastVisibleProgressPercentage !== percentage) {
          callNextObservers(this.isLoadingVisibleSubscriptions, {
            description: this._lastVisibleDescription,
            percentage: (this._lastVisibleProgressPercentage = percentage),
          });
        }
      }
    }
  }

  //To minimize load on observers (react useState setters):
  //* Only call observers IF there is a change to visibility
  //* And remember to debounce updates
  private loadingVisibleErrorObserver(err: unknown) {
    callErrorObservers(this.isLoadingVisibleSubscriptions, err);
  }
}

export type SimpleProgressControllerOptions = BaseProgressControllerOptions & {
  expectedMs?: number;
  extendAmountMs?: number;
  todoCount?: number;
  start?: boolean;
};

//SimpleProgressController is for tracking progress of some async work
//* Call this.start() to start tracking progress
//* Optionally call this.addProgress() when you want to track incremental progress towards the async work
//* Call this.done() to stop
//* Call this.error(err) if there was an async error that caused progress to stop prematurely (before it finished)
export class SimpleProgressController extends BaseProgressController {
  #expectedMs = 0; //estimated/expected milliseconds to complete
  #extendAmountMs = 0; //Amount to extend expectation from `now` when progress exceeds this.expectedMs
  _doneCount = 0; //default;
  _todoCount = 1; //default;
  _isStalled = false;

  constructor(options?: SimpleProgressControllerOptions) {
    super(options);
    if (options) {
      if (
        typeof options.expectedMs === 'number' &&
        Number.isSafeInteger(options.expectedMs) &&
        options.expectedMs > 0
      ) {
        this.#expectedMs = options.expectedMs;
      }
      this.#extendAmountMs =
        typeof options.extendAmountMs === 'number' &&
        Number.isSafeInteger(options.extendAmountMs) &&
        options.extendAmountMs > 0
          ? options.extendAmountMs
          : //Default: If we exceeded estimatedMs, keep adding another 10% (rounded up to nearest ms)
            Math.ceil(this.#expectedMs * 0.1);

      if (
        typeof options.todoCount === 'number' &&
        Number.isSafeInteger(options.todoCount) &&
        options.todoCount > 0
      ) {
        this._todoCount = options.todoCount;
      } else if (this.#expectedMs > 0 && !options.unit) {
        this._unit = millisecondsUnit;
        this._todoCount = this.#expectedMs;
      }

      const start = options?.start;
      if (start === undefined || start === true) {
        this.start();
      }
    }
  }

  //start(): Call to signal progress started
  public start(todoCount?: number): this {
    if (!this.hasStarted) {
      if (
        typeof todoCount === 'number' &&
        Number.isSafeInteger(todoCount) &&
        todoCount > 0
      ) {
        this._todoCount = todoCount;
      }
      this._startedAt = Date.now();
    }
    return this;
  }

  //addProgress: add count units of progress towards done
  public addProgress(count: number): void {
    if (this._isActive && Number.isSafeInteger(count) && count > 0) {
      this._doneCount += count; //may be greater than todoCount
      if (this._doneCount > this._todoCount) {
        this._todoCount = this._doneCount;
      }
      if (this._doneCount === this._todoCount) {
        this.done();
      } else {
        callNextObservers(this._subscriptions, this.signal);
      }
    }
  }

  public get isStalled(): boolean {
    return this._isStalled;
  }

  public set isStalled(isStalled: boolean) {
    this._isStalled = isStalled;
  }

  public get doneCount(): number {
    if (this.finishedAt || this._doneCount > 0) {
      return this._doneCount;
    }
    //Use elapsedMs if unit is milliseconds and addProgress hasn't been called
    //* Note it is cached only if unit is millisecondsUnit. And cached so getter is not called twice
    if (this.unit === millisecondsUnit) {
      const elapsedMs = this.elapsedMs;
      if (Number.isFinite(elapsedMs)) {
        return elapsedMs;
      }
    }
    return this._doneCount;
  }

  public get todoCount(): number {
    if (this.finishedAt) {
      return this._todoCount;
    }
    const elapsedMs = this.elapsedMs;
    if (this.unit === millisecondsUnit && Number.isFinite(elapsedMs)) {
      const todoCount = this._todoCount;
      return elapsedMs > todoCount
        ? elapsedMs + this.#extendAmountMs //Taking longer, so extend todoCount
        : todoCount; //use todoCount
    }
    return this._todoCount;
  }

  public set todoCount(todoCount: number) {
    if (
      !this._finishedAt &&
      Number.isSafeInteger(todoCount) &&
      todoCount > 0 &&
      this._todoCount !== todoCount
    ) {
      this._todoCount = todoCount;
      callNextObservers(this._subscriptions, this.signal);
    }
  }

  // estimatedTimeRemainderMs: time in ms
  public get estimatedTimeRemainderMs(): number {
    if (this.finishedAt) {
      return 0;
    }
    const remainder = this.remainder;
    if (this.unit === millisecondsUnit) {
      return remainder > 0 ? remainder : this.expectedMs;
    }
    const rate = this.rate;
    return rate > 0 ? remainder / rate : this.expectedMs;
  }

  public signalObservers(): void {
    callNextObservers(this._subscriptions, this.signal);
  }

  //done(): Call to signal progress is done
  public done(): void {
    if (this._isActive && !this._finishedAt) {
      this._finishedAt = Date.now();
      if (!this._startedAt) {
        this._startedAt = this._finishedAt;
      }
      if (this._unit === millisecondsUnit) {
        this._doneCount = this._todoCount = this._finishedAt - this._startedAt;
      } else {
        this._doneCount = this._todoCount;
      }
      this._isActive = false;
      callNextAndCompleteObservers(this._subscriptions, this.signal);
      this._subscriptions.clear(); //done with subscriptions
    }
  }

  //error(): Call to signal an error occurred before work could complete
  public error(err: unknown): void {
    this._error(err);
  }

  //expectedMs: Expected time in milliseconds
  public get expectedMs(): number {
    if (this.finishedAt) {
      return this.elapsedMs;
    }
    if (this._todoCount > 0) {
      const rate = this.rate;
      if (rate > 0) {
        return this._todoCount / rate;
      }
    }
    return this.#expectedMs;
  }

  //extendAmountMs: Amount to extend expectation from `now` when progress exceeds this.expectedMs
  public get extendAmountMs(): number {
    return this.#extendAmountMs;
  }
}

//createAndStartSimpleProgress(): A factory to create SimpleProgressController and start it
export const createAndStartSimpleProgress = (
  options: SimpleProgressControllerOptions,
) => new SimpleProgressController(options).start();

//Private Helper to assert consistent unit when pushing new items to ensure the chosen default weight
//results in consistent units.
//* It should never throw when CompositeProgressController's items are composed correctly.
//* If it does throw, it is most likely to throw during development. Look at the stack trace and make sure the
//  (item * weight) map to consistent units.  Otherwise the aggregated progress will have mixed units and it won't
//  make sense.
const assertConsistentUnit = (
  unit: string | undefined,
  currentUnit: string | undefined,
): string | undefined => {
  if (typeof currentUnit === 'string' && unit !== currentUnit) {
    throw new Error(
      `Units must be consistent. Cannot add item with weight that maps to ${unit}, to CompositeProgressController with ${currentUnit}`,
    );
  }
  return unit;
};

type HasSignal = {
  signal: AnyProgressSignal;
};

type ItemAndOrWeight = {
  signal?: AnyProgressSignal;
  weight?: number;
};

export type CompositeProgressControllerOptions =
  BaseProgressControllerOptions & {
    visibleIds?: Iterable<string | undefined>;
    itemAndOrWeights?: ItemAndOrWeight[];
    expectedMs?: number;
  };

type ItemAndOptionalWeight = [AnyProgressSignal, number?];
type ItemAndWeight = [AnyProgressSignal, number];

//defaultWeight calculates the weight run 1/rate of the item
const defaultWeight = (item: AnyProgressSignal): number => {
  const { rate, expectedMs, todoCount } = item;
  if (
    (!Number.isFinite(rate) || rate === 0) &&
    typeof todoCount === 'number' &&
    todoCount > 0 &&
    expectedMs > 0
  ) {
    return expectedMs / todoCount; //expected 1/rate (not actual because it hasn't started)
  } else {
    return rate === 0 ? 0 : 1 / rate;
  }
};

//TODO:
//* Provide an initial estimate! (before children are added) so that estimate doesn't start at 0.

//CompositeProgressController is for aggregating a collection of sequential/parallel progresses
//* CompositeProgressController is not concerned with how the async work associated with each child progress
//  * The async work can be sequential or parallel.  It does not effect how the progress is aggregated
//* What is important is the weighted values for the child `todoCount` and `doneCount`.
//  And what child started first and finished last.
//* Example children items: SimpleProgressController, FetchProgress and other CompositeProgressController (anything that extends BaseProgressController)
//  * Note: CompositeProgressController can be nested with different weights
export class CompositeProgressController extends BaseProgressController {
  //Child progress signal instances by string id, and progress signals that are not by id
  //* A composite progress controller can contain progressSignals that are not included in progress calculations. Only visibleIds are included in the progress calculations.
  //  And visibility may change while loading.
  #items: Map<string, ItemAndOptionalWeight> = new Map();
  #items2: ItemAndOptionalWeight[] = [];
  #visibleIds: Set<string | undefined> = new Set(); //progressSignals in this.#items that are visible in terms of progress

  //Map of child progress instances to unsubscribe() this CompositeProgressController's observer of child
  #unsubscribes = new Map<AnyProgressSignal, Unsubscribable['unsubscribe']>();
  #isClosed = false; //isClosed for pushing new items
  #expectedMs = 0; //estimated/expected milliseconds to complete

  constructor(options?: CompositeProgressControllerOptions) {
    super(options);
    if (options) {
      if (isIterable(options.itemAndOrWeights)) {
        this.#push(...options.itemAndOrWeights);
      }
      if (isIterable(options.visibleIds)) {
        this.updateVisibleIds(options.visibleIds);
      }
      if (typeof options.expectedMs === 'number' && options.expectedMs > 0) {
        this.#expectedMs = options.expectedMs;
      }
    }
    this._startedAt = Date.now(); //placeholder until an item is pushed
  }

  //Note: dispose does not dispose of _todoCount, _doneCount (if defined)
  public disposeIfUnused() {
    this.clear();
    this.#isClosed = true;
    return super.disposeIfUnused();
  }

  public updateVisibleIds(
    visibleIds: Iterable<string | undefined>,
    clear = true,
  ) {
    const original = this.#visibleIds;
    let changed = false;
    const newIds = new Set<string | undefined>();
    for (const visibleId of visibleIds) {
      if (!newIds.has(visibleId) && typeof visibleId === 'string') {
        if (this.#items.has(visibleId)) {
          if (!changed && !original.has(visibleId)) {
            changed = true;
          }
          newIds.add(visibleId);
          original.delete(visibleId);
        } else {
          //search descendents and add item.id if a descendents is visible
          for (const [childSignal] of this.#items.values()) {
            if (!newIds.has(childSignal.id)) {
              for (const descendent of childSignal.descendents()) {
                if (visibleId === descendent.id) {
                  if (!changed && !original.has(visibleId)) {
                    changed = true;
                  }
                  newIds.add(visibleId);
                  original.delete(visibleId);
                  break;
                }
              }
            }
          }
        }
      }
    }
    if (clear) {
      changed = changed || original.size > 0;
    } else {
      for (const id of original) {
        newIds.add(id);
      }
    }
    this.#visibleIds = newIds;
    if (changed) {
      callNextObservers(this._subscriptions, this.signal);
    }
  }

  public appendVisibleIds(visibleIds: Iterable<string | undefined>) {
    let changed = false;
    for (const visibleId of visibleIds) {
      if (!this.#visibleIds.has(visibleId) && typeof visibleId === 'string') {
        if (this.#items.has(visibleId)) {
          if (!changed) {
            changed = true;
          }
          this.#visibleIds.add(visibleId);
        } else {
          //search descendents and add item.id if a descendents is visible
          for (const [childSignal] of this.#items.values()) {
            if (!this.#visibleIds.has(childSignal.id)) {
              for (const descendent of childSignal.descendents()) {
                if (visibleId === descendent.id) {
                  if (!changed) {
                    changed = true;
                  }
                  this.#visibleIds.add(visibleId);
                  break;
                }
              }
            }
          }
        }
      }
    }
    if (changed) {
      callNextObservers(this._subscriptions, this.signal);
    }
  }

  //pushFetchProgress(): push a new FetchProgressController
  public pushFetchProgress(
    options: FetchProgressControllerOptions,
  ): FetchProgressController {
    if (this.#isClosed) {
      throw new Error(
        `Can't push FetchProgressController to closed CompositeProgressController`,
      );
    } else {
      //eslint-disable-next-line @typescript-eslint/no-use-before-define
      const progress = new FetchProgressController(options);
      this.#push({ signal: progress.signal });
      return progress;
    }
  }

  //pushSimpleProgress(): push a new SimpleProgressController
  public pushSimpleProgress(
    options: SimpleProgressControllerOptions,
  ): SimpleProgressController {
    if (this.#isClosed) {
      throw new Error(
        `Can't push SimpleProgressController to closed CompositeProgressController`,
      );
    } else {
      const progress = new SimpleProgressController(options);
      this.#push({ signal: progress.signal });
      return progress;
    }
  }

  //pushCompositeProgress(): push a new CompositeProgressController
  public pushCompositeProgress(
    options?: CompositeProgressControllerOptions,
  ): CompositeProgressController {
    if (this.#isClosed) {
      throw new Error(
        `Can't push CompositeProgressController to closed CompositeProgressController`,
      );
    } else {
      const progress = new CompositeProgressController(options);
      this.#push({ signal: progress.signal });
      return progress;
    }
  }

  //Close(): Close this CompositeProgressController from being able to push new child progress items
  public close(): this {
    //Once closed, it stays closed
    this.#isClosed = true;
    if (this.#items.size === 0 && this.#items2.length === 0) {
      this._finishedAt = Date.now();
      if (this._doneCount == null || this._todoCount == null) {
        const unit = this.unit;
        if (!unit || unit === millisecondsUnit) {
          this._unit = millisecondsUnit; //force units to time in case undefined
          const finishedAt = this.finishedAt;
          const startedAt = this.startedAt;
          if (finishedAt && startedAt) {
            this._doneCount = this._todoCount = finishedAt - startedAt;
          }
        }
        if (this._doneCount == null || this._todoCount == null) {
          this._doneCount = this._todoCount = this.todoCount;
        }
      }
      if (!this._startedAt) {
        this._startedAt = this._finishedAt; //fallback
      }
    }
    if (this.finishedAt) {
      this._startedAt = this._startedAt ?? this.startedAt;
      callCompleteObservers(this._subscriptions);
      this.clearIfFinished();
    }
    return this;
  }

  //isClosed: Is this CompositeProgressController closed for pushing new items
  public get isClosed(): boolean {
    return this.#isClosed;
  }

  //isOpen: Is this CompositeProgressController open for pushing new items
  public get isOpen(): boolean {
    return !this.#isClosed;
  }

  // isActive: boolean
  public get isActive(): boolean {
    const visibleIds = this.#visibleIds;
    return (
      !this.#isClosed ||
      some(
        this.#items.entries(),
        ([id, [childSignal]]) => visibleIds.has(id) && childSignal.isActive,
      ) ||
      (visibleIds.has(undefined) &&
        this.#items2.some(([childSignal]) => childSignal.isActive))
    );
  }

  public get isStalled(): boolean {
    const visibleIds = this.#visibleIds;
    const isStalled =
      !this.#isClosed &&
      (some(
        this.#items.entries(),
        ([id, [childSignal]]) => visibleIds.has(id) && !!childSignal.isStalled,
      ) ||
        (visibleIds.has(undefined) &&
          this.#items2.some(([childSignal]) => childSignal.isStalled)));
    return isStalled;
  }

  //length: Number of items in this CompositeProgressController
  public get length(): number {
    return this.#items.size + this.#items2.length;
  }

  public get visibleLength(): number {
    const visibleIds = this.#visibleIds;
    let length = 0;
    for (const id of this.#items.keys()) {
      if (visibleIds.has(id)) {
        length++;
      }
    }
    return length + (visibleIds.has(undefined) ? this.#items2.length : 0);
  }

  public get hasItems(): boolean {
    return this._finishedAt
      ? false
      : this.#items.size > 0 || this.#items2.length > 0;
  }

  public get items(): Iterable<AnyProgressSignal> {
    const items = this.#items;
    const items2 = this.#items2;
    return {
      *[Symbol.iterator]() {
        for (const [childSignal] of chain(items.values(), items2)) {
          yield childSignal;
        }
      },
    };
  }

  public get itemWeightPairs(): Iterable<ItemAndWeight> {
    const items = this.#items;
    const items2 = this.#items2;
    return {
      *[Symbol.iterator]() {
        for (const [childSignal, weight] of chain(items.values(), items2)) {
          yield [childSignal, weight ?? defaultWeight(childSignal)];
        }
      },
    };
  }

  public get visibleIds(): Iterable<string> {
    //eslint-disable-next-line @typescript-eslint/no-this-alias
    const visibleIds = this.#visibleIds;
    return {
      *[Symbol.iterator]() {
        for (const visibleId of visibleIds) {
          yield visibleId;
        }
      },
    } as Iterable<string>;
  }

  public get visibleItems(): Iterable<AnyProgressSignal> {
    const items = this.#items;
    const items2 = this.#items2;
    const visibleIds = this.#visibleIds;
    return {
      *[Symbol.iterator]() {
        if (visibleIds.size <= items.size) {
          for (const id of visibleIds) {
            const item = items.get(id as string);
            if (item) {
              yield item[0];
            }
          }
        } else {
          for (const [id, [childSignal]] of items.entries()) {
            if (visibleIds.has(id)) {
              yield childSignal;
            }
          }
        }
        if (visibleIds.has(undefined)) {
          for (const [childSignal] of items2) {
            yield childSignal;
          }
        }
      },
    };
  }

  public get visibleItemWeightPairs(): Iterable<ItemAndWeight> {
    const items = this.#items;
    const items2 = this.#items2;
    const visibleIds = this.#visibleIds;
    return {
      *[Symbol.iterator]() {
        for (const [id, [childSignal, weight]] of items.entries()) {
          if (visibleIds.has(id)) {
            yield [childSignal, weight ?? defaultWeight(childSignal)];
          }
        }
        if (visibleIds.has(undefined)) {
          for (const [childSignal, weight] of items2) {
            yield [childSignal, weight ?? defaultWeight(childSignal)];
          }
        }
      },
    };
  }

  public get unit(): string | undefined {
    if (this._unit) {
      return this._unit;
    }

    let lastUnit;
    for (const [{ unit }] of chain(this.#items.values(), this.#items2)) {
      if (!lastUnit) {
        lastUnit = unit;
      } else if (lastUnit !== unit) {
        return undefined; //units must be consistent if this._unit is not set
      }
    }
    return lastUnit;
  }

  //expectedMs: Expected time in milliseconds
  public get expectedMs(): number {
    //Don't attempt to aggregate expectedMs of this.#items because it is unknown if they are running
    //concurrently or not.
    return this.#expectedMs;
  }

  public get estimatedTimeRemainderMs(): number {
    if (this.finishedAt) {
      return 0;
    }
    let estimatedRemainderMs = 0;
    let maxEstimatedRemainderMs;
    //careful: signal.finishedAt could be recursive.
    //* item could be CompositeProgressController (but will be finished or empty)
    //  so should be safe.
    for (const signal of this._leafVisibleDescendents()) {
      if (signal.finishedAt) {
        continue; //finished, so item has 0 estimatedRemainderMs
      }
      if (signal.startedAt) {
        if (
          !maxEstimatedRemainderMs ||
          signal.estimatedTimeRemainderMs > maxEstimatedRemainderMs
        ) {
          maxEstimatedRemainderMs = signal.estimatedTimeRemainderMs;
        }
      } else {
        //hasn't started (assuming it will not be started concurrently)
        estimatedRemainderMs += signal.expectedMs;
      }
    }
    estimatedRemainderMs +=
      typeof maxEstimatedRemainderMs === 'number' &&
      Number.isFinite(maxEstimatedRemainderMs)
        ? maxEstimatedRemainderMs
        : 0;

    return estimatedRemainderMs > 0 ? estimatedRemainderMs : this.#expectedMs;
  }

  // todoCount: Expected number of todo items (unit is not specified in CompositeProgressController)
  // * Note this is a weighted count of the child progresses
  public get todoCount(): number {
    if (this._todoCount != null) {
      return this._todoCount; //only defined if this.finishedAt is defined
    }
    if (this.unit === millisecondsUnit) {
      const finishedAt = this.finishedAt;
      const startedAt = this.startedAt;
      return finishedAt && startedAt
        ? finishedAt - startedAt
        : startedAt
        ? Date.now() - startedAt + this.estimatedTimeRemainderMs
        : this.expectedMs;
    }

    let todoCount = 0;
    if (this.#items.size === 0 && this.#items2.length === 0) {
      //no items added to controller yet. so start with these defaults
      todoCount = 1;
    } else {
      for (const [childSignal, weight] of this._leafVisibleItemWeightPairs()) {
        const itemTodoCount = childSignal.todoCount || 1; //minimum is the item
        todoCount += itemTodoCount * weight;
      }
    }
    if (this.finishedAt) {
      //memoize for performance. For large composite (depth/breadth) progress controllers, this reduces
      //need to iterate over this._leafVisibleItemWeightPairs()
      this._todoCount = todoCount;
    }
    return todoCount;
  }

  public get doneCount(): number {
    if (this._doneCount != null) {
      return this._doneCount; //only defined if this.finishedAt is defined
    }
    if (this.unit === millisecondsUnit) {
      //Don't aggregate items because some may be running concurrently and double counting must be avoided
      const startedAt = this.startedAt;
      return startedAt ? (this.finishedAt || Date.now()) - startedAt : 0;
    }

    let doneCount = 0;
    for (const [childSignal, weight] of this._leafVisibleItemWeightPairs()) {
      const itemDoneCount = childSignal.doneCount || 0;
      if (itemDoneCount === 0 || !Number.isFinite(itemDoneCount)) {
        continue;
      }
      doneCount += itemDoneCount * weight;
    }
    if (this.finishedAt) {
      //memoize for performance. For large composite (depth/breadth) progress controllers, this reduces
      //need to iterate over this._leafVisibleItemWeightPairs()
      return (this._doneCount = this._todoCount = this.todoCount);
    }
    return doneCount;
  }

  public get progressRatio(): number {
    if (this._finishedAt) {
      return 1;
    }
    let todoCount = 0;
    let doneCount = 0;
    if (this.unit === millisecondsUnit) {
      let hasMaxFinishedAt = !this.#isClosed;
      let maxFinishedAt: number | undefined;
      let minStartedAt: number | undefined;
      for (const childSignal of this._leafVisibleDescendents()) {
        const { startedAt } = childSignal;
        if (startedAt && (!minStartedAt || startedAt < minStartedAt)) {
          minStartedAt = startedAt;
        }
        if (hasMaxFinishedAt) {
          const { finishedAt } = childSignal;
          if (!finishedAt) {
            maxFinishedAt = undefined; //some item has not finished
            hasMaxFinishedAt = false;
            continue;
          }
          if (!maxFinishedAt || finishedAt > maxFinishedAt) {
            maxFinishedAt = finishedAt; //defined because iteration did not `break`
          }
        }
      }
      todoCount =
        maxFinishedAt && minStartedAt
          ? maxFinishedAt - minStartedAt
          : minStartedAt
          ? Date.now() - minStartedAt + this.estimatedTimeRemainderMs
          : this.expectedMs;
      doneCount = minStartedAt
        ? (maxFinishedAt || Date.now()) - minStartedAt
        : 0;
      if (doneCount > todoCount) {
        todoCount = doneCount; //edge case with Date.now()
      }
    } else {
      if (this.#items.size === 0 && this.#items2.length === 0) {
        //no items added to controller yet. so start with these defaults
        todoCount = 1;
        doneCount = 0;
      } else {
        for (const [
          childSignal,
          weight,
        ] of this._leafVisibleItemWeightPairs()) {
          const itemTodoCount = childSignal.todoCount || 0;
          const itemDoneCount = childSignal.doneCount || 0;
          if (itemTodoCount !== 0 && Number.isFinite(itemTodoCount)) {
            todoCount += itemTodoCount * weight;
          }
          if (itemDoneCount !== 0 && Number.isFinite(itemDoneCount)) {
            doneCount += itemDoneCount * weight;
          }
        }
      }
    }

    return todoCount > 0 ? doneCount / todoCount : 0;
  }

  protected get _isLoadingVisible(): boolean {
    if (this._finishedAt) {
      return false;
    }
    for (const item of this._leafVisibleDescendents()) {
      if (!item.finishedAt) {
        return true; //some descendent is not done
      }
    }
    return false; //could still be loading some non-visible
  }

  // startedAt: ms since unix epoch
  public get startedAt(): number | undefined {
    if (this._startedAt) {
      return this._startedAt;
    }
    let minStartedAt: number | undefined;
    for (const [{ startedAt }] of chain(this.#items.values(), this.#items2)) {
      if (startedAt && (!minStartedAt || startedAt < minStartedAt)) {
        minStartedAt = startedAt;
      }
    }
    return minStartedAt;
  }

  // finishedAt: ms since unix epoch
  // * Because additional items can be pushed to `this`, finishedAt is only defined
  //   if this.#isClosed && every child item is finished.
  // * If defined, finishedAt is the maximum (latest) finishedAt time of its items
  public get finishedAt(): number | undefined {
    if (this._finishedAt) {
      return this._finishedAt;
    }
    let maxFinishedAt: number | undefined;
    if (this.#isClosed) {
      for (const [childSignal] of chain(this.#items.values(), this.#items2)) {
        //Careful: finishedAt can be a recursive call
        const { finishedAt } = childSignal;
        if (!finishedAt) {
          return undefined; //some item has not finished
        }
        if (!maxFinishedAt || finishedAt > maxFinishedAt) {
          maxFinishedAt = finishedAt; //defined because iteration did not `break`
        }
      }
    }
    if (maxFinishedAt) {
      //memoize for performance. For large composite (depth/breadth) progress controllers, this reduces
      //need to iterate over this._leafVisibleItemWeightPairs()
      this._finishedAt = maxFinishedAt;
      this._startedAt = this._startedAt ?? this.startedAt;
      this._doneCount = this._todoCount = this.todoCount;
      this.clearIfFinished();
    }
    return maxFinishedAt;
  }

  public get description(): string {
    let firstUnfinishedItem;
    let lastFinishedItem;
    let maxFinishedAt: number | undefined;
    for (const item of this._leafVisibleDescendents()) {
      if (!item.startedAt) {
        continue;
      }
      const finishedAt = item.finishedAt;
      if (!finishedAt) {
        firstUnfinishedItem = item;
        break;
      }
      if (!maxFinishedAt || finishedAt > maxFinishedAt) {
        lastFinishedItem = item;
        maxFinishedAt = finishedAt;
      }
    }

    return (
      (firstUnfinishedItem || lastFinishedItem)?.description ??
      (typeof this._description === 'string'
        ? this._description
        : typeof this._description === 'function'
        ? this._description(this)
        : '')
    );
  }

  //push(): Pushes new progress items to end
  //* Only pushable progress items are pushed. (others are filtered)
  //* Like Array.prototype.push(), returns the new length
  public push(...newItemAndOrWeights: ItemAndOrWeight[]): number {
    if (!this.#isClosed) {
      return this.#push(...newItemAndOrWeights);
    }
    return this.length;
  }

  public remove(...items: Partial<HasSignal>[]): number {
    if (!this.#isClosed) {
      return this.#remove(...items);
    }
    return this.length;
  }

  //clear(): Remove all progress items
  public clear(clearDescendants = true): void {
    if (!this._finishedAt) {
      this._finishedAt = this.finishedAt; //establish finishedAt, todoCount, doneCount
    }
    for (const [childSignal] of chain(this.#items.values(), this.#items2)) {
      childSignal.clear(clearDescendants);
      childSignal.removeUsedBy(this);
      childSignal.disposeIfUnused();
    }
    this.#unsubscribes.forEach((unsubscribe) => {
      unsubscribe();
    });
    this.#unsubscribes.clear();
    this.#items.clear();
    this.#items2.length = 0;
    if (this._finishedAt) {
      super.disposeIfUnused();
    }
  }

  public clearIfFinished(clearDescendants = true): void {
    if (!this._finishedAt) {
      this._finishedAt = this.finishedAt; //establish finishedAt, todoCount, doneCount
    }
    if (this._finishedAt) {
      for (const [childSignal] of chain(this.#items.values(), this.#items2)) {
        childSignal.clearIfFinished(clearDescendants);
        childSignal.removeUsedBy(this);
        childSignal.disposeIfUnused();
      }
      this.#unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
      this.#unsubscribes.clear();
      this.#items.clear();
      this.#items2.length = 0;
      super.disposeIfUnused();
    }
  }

  public clearDone(): void {
    const finishedAt = this.finishedAt;
    if (finishedAt) {
      this.signal.removeFromUsedBy(); //remove self from others
      this.clear();
      return;
    }
    for (const [childSignal] of chain(this.#items.values(), this.#items2)) {
      childSignal.clearIfFinished();
    }
  }

  public subscribe(observer: Partial<Observer<ProgressSignal<this>>>) {
    if (
      this.isActive &&
      observer &&
      this.length > 0 &&
      this._subscriptions.size === 0
    ) {
      //Because we're about to add first subscription, subscribe to children
      //* would like to subscribe to leaf_descendents... but can't because new descendents could be
      //  added in future. Children can be added in future too. (and they are subscribed to when that happens)
      //  But currently we don't track ancestors, so can't iterate over ancestors when a new descendent is added
      for (const [childSignal] of chain(this.#items.values(), this.#items2)) {
        this._subscribeChild(childSignal);
      }
    }
    return super.subscribe(observer);
  }

  public *descendents(): IterableIterator<AnyProgressSignal> {
    //Depth first traversal with stack (rather than recursive solution so stack isn't blown)
    for (const child of this.signal?.items ?? []) {
      const stack: AnyProgressSignal[] = [child];
      let item: AnyProgressSignal | undefined;
      //eslint-disable-next-line no-cond-assign
      while ((item = stack.pop())) {
        yield item;
        if (item.isComposite) {
          for (const child of item.items) {
            stack.push(child);
          }
        }
      }
    }
  }

  private _subscribeChild(item: AnyProgressSignal): void {
    if (!this.#unsubscribes.has(item) && this.signal) {
      this.#unsubscribes.set(
        item,
        item.subscribe({
          next: () => {
            callNextObservers(this._subscriptions, this.signal);
          },
          complete: () => {
            //If every item is finished, then this is finished
            if (this.finishedAt) {
              //Every item is finished after the current item finished
              //So must call complete() observers
              callCompleteObservers(this._subscriptions);
              this._subscriptions.clear(); //done with our subscriptions
            }
          },
          error: (err: unknown) => {
            //If this child item has an error, the error will propogate to this CompositeProgressController
            //* But it won't cause this CompositeProgressController to be marked inactive or closed, because
            //  the async work that is being tracked by this CompositeProgressController may handle the error
            //  and proceed with additional async work to be tracked by this CompositeProgressController.
            //* This controller does not make assumptions about how failed async work will be handled that it
            //  is tracking progress for.
            this._error(err, false);
            if (item) {
              item.removeUsedBy(this);
            }
            this.#items.delete(item.id as string);
          },
        }).unsubscribe,
      );
    }
  }

  // private *_leafDescendents(): IterableIterator<AnyProgressSignal> {
  //   if (this.signal) {
  //     //Depth first traversal with stack (rather than recursive solution so stack isn't blown)
  //     const stack: AnyProgressSignal[] = [this.signal];
  //     let signal: AnyProgressSignal | undefined;
  //     // eslint-disable-next-line no-cond-assign
  //     while ((signal = stack.pop())) {
  //       if (signal.hasItems) {
  //         stack.push(...signal.items);
  //       } else {
  //         yield signal; //could be a non-Composite or a composite that is finished or has no items yet, (and therefore leaf)
  //       }
  //     }
  //   }
  // }

  private *_leafVisibleDescendents(): IterableIterator<AnyProgressSignal> {
    if (this.signal) {
      //Depth first traversal with stack (rather than recursive solution so stack isn't blown)
      for (const childSignal of this.signal.visibleItems) {
        const stack: AnyProgressSignal[] = [childSignal];
        let signal: AnyProgressSignal | undefined;
        //eslint-disable-next-line no-cond-assign
        while ((signal = stack.pop())) {
          if (signal.hasItems) {
            stack.push(...signal.items);
          } else {
            yield signal; //could be a non-Composite or a composite that is finished or has no items yet, (and therefore leaf)
          }
        }
      }
    }
  }

  private *_leafVisibleItemWeightPairs(): IterableIterator<ItemAndWeight> {
    if (this.signal) {
      //Depth first traversal with stack (rather than recursive solution so stack isn't blown)
      for (const childPair of this.signal.visibleItemWeightPairs) {
        const stack: [AnyProgressSignal, number][] = [childPair];
        let pair: [AnyProgressSignal, number] | undefined;
        //eslint-disable-next-line no-cond-assign
        while ((pair = stack.pop())) {
          const [childSignal, lastWeight] = pair;
          if (childSignal.hasItems) {
            for (const [child, weight] of childSignal.itemWeightPairs) {
              stack.push([child, lastWeight * weight]);
            }
          } else {
            yield pair; //could be a non-Composite or a composite that is finished or has no items yet, (and therefore leaf)
          }
        }
      }
    }
  }

  //Private: #push()
  //* Should only be called if !this.#isClosed
  #push(...newItemAndOrWeights: ItemAndOrWeight[]): number {
    let pushed = false;
    for (const newItemAndOrWeight of newItemAndOrWeights) {
      const { signal, weight } = newItemAndOrWeight || {};
      if (
        !signal ||
        !(signal instanceof ProgressSignal) ||
        this.#items.has(signal.id as string) ||
        this.#items2.some(([childSignal]) => childSignal === signal) ||
        !signal.isActive
      ) {
        continue;
      }
      const id = signal.id;
      if (typeof weight === 'number') {
        if (typeof id === 'string') {
          this.#items.set(id, [signal, weight]);
        } else {
          this.#items2.push([signal, weight]);
        }
      } else {
        //Will use the defaultWeight getter, which calculates 1/rate of the item
        //To use the defaultWeight getter, there is an assumption that `this._unit === millisecondsUnit`, which we assert below
        this._unit = assertConsistentUnit(millisecondsUnit, this._unit);
        if (typeof id === 'string') {
          this.#items.set(id, [signal]);
        } else {
          this.#items2.push([signal]);
        }
      }
      if (this._subscriptions.size > 0) {
        this._subscribeChild(signal);
      }
      signal.addUsedBy(this);
      pushed = true;
    }
    if (pushed) {
      //Reset startedAt
      delete this._startedAt;
      //Call nextObservers so `this` CompositeProgressController's observers can get latest progress
      callNextObservers(this._subscriptions, this.signal);
    }
    return this.length;
  }

  #remove(...items: Partial<HasSignal>[]): number {
    let removed = false;
    for (const item of items) {
      const { signal } = item || {};
      if (!signal || !(signal instanceof ProgressSignal) || !signal.isActive) {
        continue;
      }
      signal.removeUsedBy(this);
      removed = this.#items.delete(signal.id as string) || removed;
      const items2Index = this.#items2.findIndex(
        ([childSignal]) => childSignal === signal,
      );
      if (items2Index > -1) {
        this.#items2.splice(items2Index, 1);
        removed = true;
      }

      const unsubscribe = this.#unsubscribes.get(signal);
      if (unsubscribe) {
        unsubscribe();
        removed = true;
      }
      this.#visibleIds.delete(signal.id);
      signal.disposeIfUnused();
    }
    if (removed) {
      //Call nextObservers so `this` CompositeProgressController's observers can get latest progress
      callNextObservers(this._subscriptions, this.signal);
    }
    return this.length;
  }
}

type MessageForFetchError =
  | string
  | ((status: number, statusText: string) => string);

const defaultMessageForFetchError: MessageForFetchError = (
  status: number,
  statusText: string,
) =>
  `Error Fetching Data: Status: ${status}${
    statusText ? `: ${statusText}` : ''
  }`;

type FetchResponseProgressControllerOptions = BaseProgressControllerOptions & {
  messageForFetchError?: MessageForFetchError;
  response?: Response;
  estimatedCompressFactor?: number;
};

//FetchResponseProgressController
//* Tracks progress of what-wg fetch `response`
//* Progress can be observed with `this.signal` a `ProgressSignal`
class FetchResponseProgressController extends BaseProgressController {
  public readonly expectedMs = 100;

  //messageForFetchError can be fn, or string
  #messageForFetchError: MessageForFetchError = defaultMessageForFetchError;
  #estimatedCompressFactor = 1.0; //gzip is ~4.5, br is ~5.5

  constructor(options?: FetchResponseProgressControllerOptions) {
    super({ ...options, unit: 'bytes' }); //todoCount, doneCount and remainderCount are bytes
    if (options) {
      if (options.messageForFetchError) {
        this.#messageForFetchError = options.messageForFetchError;
      }
      if (options.response) {
        this.start(options.response);
      }
      if (
        options.estimatedCompressFactor &&
        options.estimatedCompressFactor > 1
      ) {
        this.#estimatedCompressFactor = options.estimatedCompressFactor;
      }
      if (options.messageForFetchError) {
        this.#messageForFetchError = options.messageForFetchError;
      }
    }
  }

  public get isStalled() {
    //Can it be determined?
    return false;
  }

  public get estimatedTimeRemainderMs(): number {
    if (this.finishedAt) {
      return 0;
    }
    const remainder = this.remainder;
    if (this.unit === millisecondsUnit) {
      return remainder > 0 ? remainder : 0;
    }
    const rate = this.rate;
    return rate > 0 ? remainder / rate : 0;
  }

  public get bytesPerSecond(): number {
    return this.rateInSeconds;
  }

  public get kbPerSecond(): number {
    return this.rateInSeconds / 1024;
  }

  public get mbPerSecond(): number {
    return this.rateInSeconds / 1048576;
  }

  public done(): void {
    if (this._isActive && !this._finishedAt) {
      this._isActive = false;
      this._finishedAt = Date.now();
      if (!this._startedAt) {
        this._startedAt = this._finishedAt;
      }
      if (!this._todoCount) {
        //fallback to millisecondsUnit
        this._unit = millisecondsUnit;
        this._todoCount = Math.max(this._finishedAt - this._startedAt, 1);
      }
      this._doneCount = this._todoCount;
      callNextAndCompleteObservers(this._subscriptions, this.signal);
      this._subscriptions.clear(); //done with subscriptions
    }
  }

  //about start()
  //* Data from the response.body (a ReadableStream) is listened to. The progress is calculated each time
  //  a chunk of data arrives, and the chunk is pushed to the new ReadableStream.
  //* Elsewhere when response.json() or response.text() etc... is called, the response.body ReadableStream
  //  is read from.
  public start(response: Response): Response {
    if (!this.hasStarted) {
      this._startedAt = Date.now();
      const { body, headers, status, statusText, ok } = response;
      if (!ok) {
        const message =
          typeof this.#messageForFetchError === 'function'
            ? this.#messageForFetchError(status, statusText)
            : typeof this.#messageForFetchError === 'string'
            ? this.#messageForFetchError
            : 'Error fetching data';
        this._error(message);
        return response;
      }
      //Note: Unfortunately we can't always read headers.get('content-encoding')
      const contentLength = headers && headers.get('content-length');
      this._todoCount = contentLength
        ? Math.ceil(parseInt(contentLength, 10) * this.#estimatedCompressFactor)
        : 0;
      callNextObservers(this._subscriptions, this.signal);
      if (body && this._todoCount > 0) {
        //Note: body.on does not exist on browser (it does exist on nodejs)
        //So create new stream that reads the body ReadableStream, tracks progress and pushes data
        //to the new stream as it arrives
        const reader = body.getReader();
        //eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const stream = new ReadableStream({
          start(controller) {
            const push = () => {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    //doneCount is uncompressed value
                    //todoCount was estimated because of compression, so update it now that we know
                    self._doneCount = self._todoCount || 0;
                    self.done();
                    controller.close();
                    return;
                  }
                  if (value) {
                    if (self._isActive && value.byteLength > 0) {
                      self._doneCount += value.byteLength;
                      if (
                        !self._todoCount ||
                        self._doneCount > self._todoCount
                      ) {
                        //todoCount is an estimate because it is calculated based on contentLength which is
                        //often compressed because of gzip.  doneCount is a count of the uncompressed bytes.
                        self._todoCount = self._doneCount + 1;
                      }
                      callNextObservers(self._subscriptions, self.signal);
                    }
                  }
                  controller.enqueue(value);
                  push();
                })
                .catch((err) => {
                  self._error(err);
                });
            };
            push(); //start reading from response.body
          },
        });

        const rejectionHandler = (err: unknown) => {
          self._error(err);
          throw err;
        };
        //Wrap the Response's methods so errors processing the stream can be observed
        const WrappedResponse = class extends Response {};
        for (const method of [
          'json',
          'text',
          'formData',
          'blob',
          'arrayBuffer',
        ]) {
          WrappedResponse.prototype[
            method as 'json' | 'text' | 'formData' | 'blob' | 'arrayBuffer'
          ] = async function () {
            return Response.prototype[
              method as 'json' | 'text' | 'formData' | 'blob' | 'arrayBuffer'
            ]
              .call(this)
              .catch(rejectionHandler);
          };
        }
        return new WrappedResponse(stream, { headers, status, statusText });
      } else {
        if (!this.finishedAt) {
          //Special case when this.todoCount === 0
          callCompleteObservers(this._subscriptions);
          this._subscriptions.clear();
          this._isActive = false;
        }
      }
    }
    return response;
  }
}

export type FetchProgressControllerOptions = BaseProgressControllerOptions & {
  estimatedCompressFactor?: number;
  messageForFetchError?: MessageForFetchError;
  requestDescription?: ProgressControllerDescription;
};

//FetchProgressController
//* Composes the 2 async parts of a fetch() into this specialized ProgressController
//* A fetch is made up of 2 async operations
//  1) `response = await fetch()`
//     => This is about making the request and waiting for the response to start
//  2) `await response.json()`, `await response.text()` etc...
//     => This is about waiting for the response to finish
//eslint-disable-next-line no-use-before-define
export class FetchProgressController extends CompositeProgressController {
  #requestProgress: SimpleProgressController | undefined;
  #responseProgress: FetchResponseProgressController | undefined;
  #requestIntervalId: ReturnType<typeof setInterval> | undefined | null;

  constructor(options?: FetchProgressControllerOptions) {
    super(options);

    const estimatedCompressFactor = options?.estimatedCompressFactor;
    const messageForFetchError = options?.messageForFetchError;
    const requestExpectedMs = 100;
    const stalledThreshold = 2 * requestExpectedMs;
    const description = options?.description;
    const requestDescription: ProgressControllerDescription =
      options && options.requestDescription
        ? options.requestDescription
        : () => {
            const elapsedMs = this.elapsedMs;
            const elapsedSec = elapsedMs / 1000;
            const thisDescription =
              typeof description === 'function'
                ? description(this)
                : typeof description === 'string'
                ? description
                : '';
            return elapsedMs > stalledThreshold
              ? `${thisDescription}: Request stalled: ${elapsedSec.toFixed(
                  1,
                )} sec`
              : thisDescription;
          };

    this.push(
      {
        //Assumption: a typical request take ~100ms.
        //* Latency to send request across country is about 50ms.
        //* So: 50ms to send request and 50ms to receive start of response = 100ms
        //* This is just an estimate. The actual time may be > or < 100ms.
        //* Sometimes requests stall, that's okay.  The estimate is extended 50ms each time
        //  the request is taking longer than estimated until it completes.
        //  * Note: An error could be thrown with status=0 (not sent), or maybe an AbortError,
        //    or non-200 status.  That's okay too.  An error will be captured and propogate to
        //    the CompositeProgressController.
        signal: (this.#requestProgress = new SimpleProgressController({
          ...(options?.id && { id: `${options.id}_fetchRequest` }),
          expectedMs: requestExpectedMs,
          extendAmountMs: requestExpectedMs / 2,
          unit: millisecondsUnit,
          description: requestDescription,
        })).signal,
      },
      {
        signal: (this.#responseProgress = new FetchResponseProgressController({
          ...(options?.id && { id: `${options.id}_fetchResponse` }),
          ...(messageForFetchError && { messageForFetchError }),
          ...(estimatedCompressFactor && { estimatedCompressFactor }),
          description: this._description,
        })).signal,
      },
    );

    this.close(); //no more items will be pushed to this CompositeProgressController
  }

  public disposeIfUnused() {
    this.#responseProgress?.signal?.removeUsedBy(this);
    this.#requestProgress?.signal?.removeUsedBy(this);
    this.#responseProgress?.disposeIfUnused();
    this.#requestProgress?.disposeIfUnused();
    if (super.disposeIfUnused()) {
      this.#responseProgress = undefined;
      this.#requestProgress = undefined;
      return true;
    }
    return false;
  }

  //Call startRequest() just before `fetch()` is called
  public startRequest() {
    if (!this.#requestProgress || !this.#requestProgress) {
      return;
    }
    this.#requestProgress.start();
    const stalledThreshold = 2 * this.#requestProgress.expectedMs;
    //If request stalls, signal observers on 500ms interval.
    this.#requestIntervalId = setInterval(() => {
      if (!this.#requestProgress) {
        clearInterval(
          this.#requestIntervalId as ReturnType<typeof setInterval>,
        );
        return;
      }
      if (this.#requestProgress.elapsedMs > stalledThreshold) {
        this.#requestProgress.isStalled = true;
      }
      this.#requestProgress.signalObservers();
    }, 500);
  }

  //Call requestError() if `await fetch()` throws err
  public requestError(err: unknown) {
    if (!this.#requestProgress) {
      return;
    }
    this.#requestProgress.error(err);
    clearInterval(this.#requestIntervalId as ReturnType<typeof setInterval>);
    this.#requestProgress.isStalled = false;
    this.#requestIntervalId = null;
  }

  //Call startResponse() with response after `const response = await fetch()` resolves.
  public startResponse(response: Response) {
    if (!this.#requestProgress || !this.#responseProgress) {
      return response;
    }
    this.#requestProgress.done();
    clearInterval(this.#requestIntervalId as ReturnType<typeof setInterval>);
    this.#requestIntervalId = null;
    this.#requestProgress.isStalled = false;
    return this.#responseProgress.start(response);
  }

  public get bytesPerSecond(): number {
    return this.#responseProgress?.bytesPerSecond ?? 0;
  }

  public get kbPerSecond(): number {
    return this.#responseProgress?.kbPerSecond ?? 0;
  }

  public get mbPerSecond(): number {
    return this.#responseProgress?.mbPerSecond ?? 0;
  }
}
